import { useContext, defineComponent, computed, h } from '@nuxtjs/composition-api';

function wrapContentPageSectionFnFactories(config) {
  return Object.keys(config.sectionTypesConfig).reduce((obj, key) => {
    var _a;
    if ((_a = config.sectionTypesConfig[key]) == null ? void 0 : _a.factory) {
      obj[key] = config.sectionTypesConfig[key].factory();
    }
    return obj;
  }, {});
}

let savedUseStrapiComposable;
function buildUseStrapiComposable(config) {
  const useStrapi = function() {
    const context = useContext();
    const $strapi = context.$strapi;
    const wrappedContentSectionsFnFactories = wrapContentPageSectionFnFactories(config);
    async function prepareContentPageSection(sections) {
      const result = await prepareContentPageSections([sections]);
      return result[0];
    }
    async function prepareContentPageSections(sections) {
      const promises = sections.map(async (section) => {
        const {sectionType, sectionConfig} = getSectionTypeConfig(section);
        let result = section;
        if (!sectionConfig.component) {
          throw new Error(`No component defined for section ${sectionType}`);
        }
        section.dynamicComponent = sectionConfig.component;
        if (wrappedContentSectionsFnFactories[sectionType]) {
          result = await wrappedContentSectionsFnFactories[sectionType](section, {...section, ...{sectionType}});
        }
        if (sectionConfig.propsMapping) {
          result.propsMapping = sectionConfig.propsMapping;
        }
        return result;
      });
      return await Promise.all(promises);
    }
    function mapContentPageSectionProps(section) {
      const sectionPropMapFn = config.sectionTypesConfig[section.sectionType].propsMapping;
      return sectionPropMapFn ? {...section, ...sectionPropMapFn(section)} : section;
    }
    async function find(entity, searchParams) {
      const res = await $strapi.find(entity, searchParams);
      const tmpRes = Array.isArray(res) ? res : [res];
      await Promise.all(tmpRes.map(async (item) => {
        if (item.sections) {
          item.sections = await prepareContentPageSections(item.sections);
        }
      }));
      return res;
    }
    function getComponent(section) {
      const sectionConfig = getSectionTypeConfig(section);
      return sectionConfig.sectionConfig.component;
    }
    function mapProps(section) {
      const sectionConfig = getSectionTypeConfig(section);
      return typeof sectionConfig.sectionConfig.propsMapping === "function" ? sectionConfig.sectionConfig.propsMapping(section) : section;
    }
    function getSectionTypeConfig(section) {
      const sectionType = Object.keys(config.sectionTypes).find((key) => config.sectionTypes[key] === section.__component);
      if (!sectionType) {
        throw new Error(`Section ${section.__component} is not defined in SectionTypes`);
      }
      const sectionConfig = config.sectionTypesConfig[sectionType];
      return {sectionType, sectionConfig};
    }
    return {
      find,
      prepareContentPageSection,
      prepareContentPageSections,
      mapContentPageSectionProps,
      getComponent,
      mapProps
    };
  };
  savedUseStrapiComposable = useStrapi;
  return useStrapi;
}

const StrapiContentSection = defineComponent({
  name: "StrapiContentSection",
  props: {
    section: {
      type: Object,
      required: true
    },
    componentProps: {
      type: Object,
      default: () => ({})
    }
  },
  setup: function(props) {
    const {getComponent, mapProps} = savedUseStrapiComposable();
    const component = computed(() => getComponent(props.section));
    const mappedProps = computed(() => mapProps(props.section));
    return () => {
      return component.value && props.section ? h("div", {
        class: "strapi-content-wrapper"
      }, [h(component.value, {props: {...props.componentProps, ...mappedProps.value}})]) : null;
    };
  }
});

export { StrapiContentSection, buildUseStrapiComposable, savedUseStrapiComposable };
